import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Users, Gift, ArrowRight, Sparkles, Flame } from "lucide-react";
import { PAGE_NAME } from "../../types";
import eventBus from "../../utils/eventBus";

interface ReferralReminderProps {
  referralCount: number;
  onDismiss: () => void;
}

const ReferralReminder: React.FC<ReferralReminderProps> = ({ referralCount, onDismiss }) => {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(true);

  // Enter fullscreen when modal opens
  useEffect(() => {
    eventBus.emit("fullscreen:enter");
    return () => {
      eventBus.emit("fullscreen:exit");
    };
  }, []);

  // Calculate progress toward milestones
  const getProgressMessage = () => {
    if (referralCount < 30) {
      const remaining = 30 - referralCount;
      if (remaining <= 5) return { urgent: true, text: `Only ${remaining} more friends = 3 Days PRO! 🔥` };
      return { urgent: false, text: `Invite ${remaining} friends to unlock 3 Days PRO` };
    }
    if (referralCount < 100) {
      const remaining = 100 - referralCount;
      if (remaining <= 10) return { urgent: true, text: `${remaining} more = 1 WEEK PRO! You're so close! 💪` };
      return { urgent: false, text: `${remaining} more friends for 1 Week PRO` };
    }
    if (referralCount < 500) {
      const remaining = 500 - referralCount;
      if (remaining <= 50) return { urgent: true, text: `${remaining} more = LIFETIME PRO! Don't stop! 🚀` };
      return { urgent: false, text: `${remaining} more for Lifetime PRO` };
    }
    return { urgent: false, text: "You're a referral legend! 👑" };
  };

  const progress = getProgressMessage();

  const handleInvite = () => {
    setIsVisible(false);
    eventBus.emit("fullscreen:exit");
    setTimeout(() => {
      navigate(PAGE_NAME.INVITE_USERS);
      onDismiss();
    }, 200);
  };

  const handleClose = () => {
    setIsVisible(false);
    eventBus.emit("fullscreen:exit");
    setTimeout(onDismiss, 200);
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-4"
          onClick={handleClose}
        >
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ type: "spring", damping: 25 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-500 rounded-3xl p-1 shadow-2xl"
          >
            <div className="bg-slate-900 rounded-[22px] p-6 relative overflow-hidden">
              {/* Background Decoration */}
              <div className="absolute top-0 right-0 w-32 h-32 bg-yellow-500/20 rounded-full blur-3xl -z-0" />
              <div className="absolute bottom-0 left-0 w-24 h-24 bg-purple-500/20 rounded-full blur-2xl -z-0" />
              
              {/* Close Button */}
              <button 
                onClick={handleClose}
                className="absolute top-4 right-4 p-1.5 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
              >
                <X size={16} className="text-white/70" />
              </button>

              {/* Header */}
              <div className="relative z-10 text-center mb-4">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/20 text-amber-400 text-xs font-bold mb-3">
                  <Flame size={14} className="animate-pulse" />
                  You're Missing Out!
                </div>
                <h2 className="text-2xl font-black text-white mb-1">
                  Invite Friends & Earn!
                </h2>
              </div>

              {/* Reward Highlight */}
              <div className="relative z-10 bg-white/5 rounded-2xl p-4 mb-4 border border-white/10">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-3 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500">
                      <Gift size={24} className="text-white" />
                    </div>
                    <div>
                      <p className="text-white/60 text-xs">Earn per friend</p>
                      <p className="text-2xl font-black text-white">200 ATH + 5 Pi</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-white/60 text-xs">+ Commission</p>
                    <p className="text-lg font-bold text-emerald-400">10%</p>
                  </div>
                </div>
              </div>

              {/* Progress Message */}
              <div className={`relative z-10 p-3 rounded-xl mb-4 ${
                progress.urgent 
                  ? "bg-gradient-to-r from-red-500/20 to-orange-500/20 border border-red-500/30" 
                  : "bg-white/5 border border-white/10"
              }`}>
                <div className="flex items-center gap-2">
                  {progress.urgent ? (
                    <Sparkles size={16} className="text-amber-400 animate-pulse" />
                  ) : (
                    <Users size={16} className="text-blue-400" />
                  )}
                  <p className={`text-sm font-medium ${
                    progress.urgent ? "text-amber-300" : "text-white/80"
                  }`}>
                    {progress.text}
                  </p>
                </div>
              </div>

              {/* Your Stats */}
              <div className="relative z-10 flex justify-between items-center mb-5 px-2">
                <div className="text-center">
                  <p className="text-[10px] text-white/40 uppercase font-bold">Your Referrals</p>
                  <p className="text-xl font-black text-white">{referralCount}</p>
                </div>
                <div className="h-8 w-px bg-white/10" />
                <div className="text-center">
                  <p className="text-[10px] text-white/40 uppercase font-bold">Total Earned</p>
                  <p className="text-xl font-black text-emerald-400">{referralCount * 200} ATH {referralCount > 0 ? `+ ${referralCount * 5} Pi` : ""}</p>
                </div>
                <div className="h-8 w-px bg-white/10" />
                <div className="text-center">
                  <p className="text-[10px] text-white/40 uppercase font-bold">Rank</p>
                  <p className="text-xl font-black text-purple-400">
                    {referralCount >= 100 ? "🏆" : referralCount >= 30 ? "⭐" : "🌱"}
                  </p>
                </div>
              </div>

              {/* CTA Button */}
              <motion.button
                onClick={handleInvite}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="relative z-10 w-full py-4 rounded-2xl bg-gradient-to-r from-amber-400 via-orange-500 to-pink-500 text-white font-black text-lg flex items-center justify-center gap-2 shadow-lg shadow-orange-500/30"
              >
                Invite Friends Now
                <ArrowRight size={20} />
              </motion.button>

              {/* Social Proof */}
              <p className="relative z-10 text-center text-[11px] text-white/40 mt-4">
                🔥 <span className="text-white/60 font-medium">127 users</span> invited friends in the last hour
              </p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ReferralReminder;
